<template>
  <section class="[ max-w-7xl m-auto min-h-full flex flex-col gap-6 ]">

    <!-- Page header -->
    <div class="[ flex items-center justify-between ]">
      <h2 class="[ text-xl font-semibold ]">Scorecard Templates</h2>
      <Button label="New Template" @click="showDialog = true">
        <template #icon>
          <JBIcon name="add" />
        </template>
      </Button>
    </div>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="[ flex-1 flex flex-col items-center justify-center ]"
    >
      <JBLoadingSpinner message="Loading scorecard templates" />
    </div>

    <!-- Data table -->
    <DataTable v-else :value="items" striped-rows>
      <Column field="name" header="Template Name" sortable />
      <Column field="rulesCount" header="Rules" sortable>
        <template #body="{ data }">
          {{ data.rulesCount }} rules
        </template>
      </Column>
      <Column field="version" header="Version" sortable />
      <Column field="status" header="Status" sortable>
        <template #body="{ data }">
          <span
            class="[ inline-block text-xs font-medium px-2 py-0.5 rounded-full ]"
            :class="statusClass(data.status)"
          >
            {{ data.status }}
          </span>
        </template>
      </Column>
      <Column header="" style="width: 5rem">
        <template #body="{ data }">
          <Button
            label="Edit"
            severity="secondary"
            text
            size="small"
            @click="onEdit(data)"
          />
        </template>
      </Column>
    </DataTable>

    <!-- Edit dialog -->
    <Dialog
      v-model:visible="showDialog"
      modal
      :header="editingItem ? 'Edit Template' : 'New Template'"
      class="[ w-full max-w-lg ]"
    >
      <div class="[ flex flex-col gap-4 ]">
        <label class="[ flex flex-col gap-1 ]">
          <span class="[ text-sm font-medium ]">Template Name</span>
          <InputText v-model="formName" placeholder="e.g. 2026 QA Script" />
        </label>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="closeDialog"
        />
        <Button label="Save" @click="onSave" />
      </template>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Button, Column, DataTable, Dialog, InputText } from "primevue";
import { useMockCrud } from "@/mocks/composables/useMockApi";
import {
  generateScorecardTemplates,
  type ScorecardTemplate,
} from "@/mocks/mock-data/scorecard-templates";
import JBLoadingSpinner from "@/components/ui/JBLoadingSpinner.vue";

definePage({
  alias: "/v3/scorecard-templates",
  meta: {
    title: "Scorecard Templates",
  },
});

const { items, loading } = useMockCrud(generateScorecardTemplates(10));

const showDialog = ref(false);
const editingItem = ref<ScorecardTemplate | null>(null);
const formName = ref("");

function statusClass(status: ScorecardTemplate["status"]) {
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    draft: "bg-gray-100 text-gray-600",
    deactivated: "bg-red-100 text-red-700",
  };
  return map[status] ?? "";
}

function onEdit(item: ScorecardTemplate) {
  editingItem.value = item;
  formName.value = item.name;
  showDialog.value = true;
}

function closeDialog() {
  showDialog.value = false;
  editingItem.value = null;
  formName.value = "";
}

function onSave() {
  closeDialog();
}
</script>
